import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IndentDecrease, IndentIncrease, ListIndentDecrease, ListIndentIncrease, Trash2 } from 'lucide-react'
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuSeparator,
} from '@/src/renderer/components/ui/context-menu'
import {
  TaskStartIcon,
  TaskDeadlineIcon,
  TaskAttentionIcon,
  DurationIcon,
  TagIcon,
  MotivationIcon,
  RecurringIcon,
} from '@/src/renderer/components/icons'
import { DormantIcon } from '@/src/renderer/components/icons/DormantIcon'
import { ReactivateIcon } from '@/src/renderer/components/icons/ReactivateIcon'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { useShortcutText } from '@/src/renderer/hooks/useShortcutText'
import type { AttentionLevel, RecurrenceRule } from '@/src/renderer/lib/db/models'
import { recurrenceUpdate } from '@/src/renderer/lib/recurrence'
import type { SubtaskChanges, SubtaskItem } from '../../hooks/useSubtaskEditor'
import { useMoveTargets } from '../../hooks/useMoveTargets'
import { NestPicker } from '../NestPicker'
import type { StartValue } from '../WhenDropdown'
import type { DeadlineValue } from '../DeadlineDropdown'
import type { DurationValue, TaskEntryData } from '../TaskEntry'
import { TaskMenuTargetContent } from './TaskMenuTargetContent'
import { TaskMenuShortcutBox } from './TaskMenuShortcutBox'
import { MenuAction, MenuSub, RecurrenceLockedRow, ReminderMenuSub } from './TaskMenuRows'
import { useTaskMenuSubs } from './useTaskMenuSubs'
import { taskMenuMarks, type TaskMenuMarks } from './taskMenuMarks'
import { TASK_MENU_SHORTCUTS } from './taskMenuShortcuts'
import type { TaskMenuHandlers, TaskMenuTarget } from './TaskMenu.types'

export interface SubtaskMenuProps {
  /** Die Zeile, auf die geklickt wurde — sie liefert die Werte der Unter-Menüs. */
  subtask: SubtaskItem
  /** Was die ganze Auswahl trägt (`taskMenuMarks`); ohne zählt die Zeile allein. */
  marks?: TaskMenuMarks
  /** Schreibt eine Änderung — an die ganze Auswahl, das entscheidet der Aufrufer. */
  onUpdate: (changes: SubtaskChanges) => void
  /**
   * **Ruhen lassen** (`true`) oder **Aufwecken** (`false`) — eigenes Ruhen (#205). Fehlt es,
   * fehlt die Zeile.
   */
  onRest?: (rest: boolean) => void
  /** Ob alle gemeinten Zeilen ruhen — dann heißt die Zeile „Aufwecken" (Misch-Regel wie in `TaskMenu`). */
  dormant?: boolean
  /**
   * Die Aufgabe wiederholt sich (#205): Unter ihr gilt ihr Rhythmus — die Zeile „Wiederkehrend"
   * ist gesperrt und sagt das.
   */
  parentRecurring?: boolean
  /** Eine der gemeinten Zeilen wiederholt sich: Wiederkehrende Aufgaben sind dann kein Ziel fürs Umhängen. */
  movingRecurring?: boolean
  /** **Herauslösen**, unter die Aufgabe (wie ⇧⌥↓). Fehlt es, fehlt die Zeile. */
  onUnnest?: () => void
  /** **Einrücken** (wie ⌥→, #181): unter die Zeile darüber. Fehlt es, fehlt die Zeile. */
  onIndent?: () => void
  /** **Ausrücken** (wie ⌥←, #181): eine Ebene hinauf. Fehlt es (alles auf Ebene 1), fehlt die Zeile. */
  onOutdent?: () => void
  /** Die Aufgabe der Checkliste — ihre Liste liefert die Ziele fürs Umhängen. */
  parentId?: string
  /** **Umhängen in …** eine andere Aufgabe. Fehlt es oder `parentId`, fehlt die Zeile. */
  onMoveTo?: (parentId: string) => void
  onDelete: () => void
  /** Die Unteraufgabe, deren Erinnerungen das Unter-Menü zeigt — nur bei EINER gemeinten Zeile. */
  reminderTaskId?: string
  /** „Datum & Uhrzeit …“ — die Aufgaben-Details mit offenem Chip „Erinnerung“. Fehlt es, fehlt die Zeile. */
  onOpenReminders?: () => void
  /** Auf und zu — die Checkliste führt danach den Caret zurück (siehe `SubtaskList`). */
  onOpenChange?: (open: boolean) => void
  /** Still, aber im Baum — etwa, solange in der Zeile geschrieben wird. */
  disabled?: boolean
  /**
   * Ein **Tastenkürzel** an der angefassten Zeile: Derselbe Kasten wie im Unter-Menü geht ohne
   * Menü auf, unter der Zeile. `null` = keiner.
   */
  shortcut?: TaskMenuTarget | null
  /** Der Kasten des Kürzels hat einen Wert geliefert oder wurde geschlossen. */
  onShortcutClose?: () => void
  /** Die Zeile selbst; sie wird zum Rechtsklick-Ziel. */
  children: React.ReactElement
}

/** Die Ziele fürs Umhängen — erst beim Aufklappen gefragt, nicht für jede Zeile der Liste. */
const MoveTargets: React.FC<{
  parentId: string
  movingRecurring: boolean
  onSelect: (parentId: string) => void
  onClose: () => void
}> = ({ parentId, movingRecurring, onSelect, onClose }) => {
  const parent = useDataSource().useTask(parentId)
  const groups = useMoveTargets(parent, movingRecurring)
  return <NestPicker groups={groups} onSelect={onSelect} onClose={onClose} />
}

/**
 * Das **Aufgaben-Menü** an einer Zeile der Checkliste (#205, CONTEXT.md) — mit den Einträgen,
 * die eine Unteraufgabe trägt: Wann, Deadline, Dauer, Wiederkehrend, Aufmerksamkeit, Tags,
 * Belohnung, dazu Ruhen lassen, Einrücken, Ausrücken (#181), Herauslösen, Umhängen und Löschen. Dieselben Zeilen und Kästen
 * wie `TaskMenu`. Fokus starten und Duplizieren fehlen.
 *
 * Den Caret gibt das Menü beim Schließen nicht selbst zurück: Die Checkliste weiß, ob die
 * Zeile noch steht oder eine Nachbarin übernimmt.
 *
 * @example
 * <SubtaskMenu subtask={item} onUpdate={changes => update(ids, changes)} onDelete={remove}>
 *   <div className="subtask-list__menu-target">…</div>
 * </SubtaskMenu>
 */
export const SubtaskMenu: React.FC<SubtaskMenuProps> = ({
  subtask, marks, onUpdate, onRest, dormant = false, parentRecurring = false, movingRecurring = false, onUnnest,
  onIndent, onOutdent, parentId, onMoveTo, onDelete, reminderTaskId, onOpenReminders, onOpenChange, disabled = false, shortcut = null, onShortcutClose, children,
}) => {
  const { t } = useTranslation()
  const shortcutText = useShortcutText()
  const { openSub, setOpenSub, close, handleContentKeyDown } = useTaskMenuSubs(parentRecurring)
  const [moveOpen, setMoveOpen] = useState(false)

  // Die Kästen sprechen `TaskEntryData`; eine Zeile der Checkliste trägt dieselben Felder.
  const entry = useMemo<TaskEntryData>(() => ({
    id: subtask.id,
    title: subtask.title,
    completed: subtask.done,
    attention: subtask.attention ?? null,
    startDate: subtask.startDate ?? null,
    startTime: subtask.startTime ?? null,
    startPeriod: subtask.startPeriod ?? null,
    dueDate: subtask.dueDate ?? null,
    dueTime: subtask.dueTime ?? null,
    duePeriod: subtask.duePeriod ?? null,
    estimatedDuration: subtask.estimatedDuration ?? null,
    durationMode: subtask.durationMode ?? null,
    tags: subtask.tags ?? [],
    rewardId: subtask.rewardId ?? null,
    recurrence: subtask.recurrence ?? null,
  }), [subtask])

  const handlers = useMemo<TaskMenuHandlers>(() => ({
    onStartChange: (value: StartValue | null) => onUpdate({
      startDate: value?.date ?? null,
      startTime: value?.time ?? null,
      startPeriod: value?.period ?? null,
    }),
    onDeadlineChange: (value: DeadlineValue | null) => onUpdate({
      dueDate: value?.date ?? null,
      dueTime: value?.time ?? null,
      duePeriod: value?.period ?? null,
    }),
    onDurationChange: (value: DurationValue | null) => onUpdate({
      estimatedDuration: value?.minutes ?? null,
      durationMode: value?.mode ?? null,
    }),
    onAttentionChange: (value: AttentionLevel | null) => onUpdate({ attention: value }),
    onTagsChange: (value: string[]) => onUpdate({ tags: value }),
    onRewardChange: (rewardId: string | null) => onUpdate({ rewardId }),
    // **Die Regel setzt das Wann** — wie an der Aufgabe (CONTEXT.md, Wiederholung).
    onRecurrenceChange: (rule: RecurrenceRule | null) => onUpdate(recurrenceUpdate(rule, new Date())),
  }), [onUpdate])

  const shown = marks ?? taskMenuMarks([subtask])

  const sub = (target: TaskMenuTarget, icon: React.ReactNode, label: string) => (
    <MenuSub
      icon={icon}
      label={label}
      shortcut={shortcutText(TASK_MENU_SHORTCUTS[target])}
      open={openSub === target}
      onOpenChange={(next) => setOpenSub(next ? target : null)}
      // Die Belohnung trägt keinen Haken, wie am Aufgaben-Menü (Belohnung ODER Ziel).
      marked={target === 'reward' ? false : shown[target]}
    >
      <TaskMenuTargetContent
        target={target}
        task={entry}
        handlers={handlers}
        // „Ruhen lassen" auch im Wann-Kasten, wie am Aufgaben-Menü — nicht, wenn schon alle ruhen.
        onRest={onRest && !dormant ? () => onRest(true) : undefined}
        onClose={close}
      />
    </MenuSub>
  )

  const canMove = !!onMoveTo && !!parentId
  const closeShortcut = useCallback(() => onShortcutClose?.(), [onShortcutClose])

  return (
    <>
    <ContextMenu onOpenChange={onOpenChange}>
      <ContextMenuTrigger asChild disabled={disabled}>{children}</ContextMenuTrigger>
      <ContextMenuContent
        className="context-menu-content--space"
        onKeyDown={handleContentKeyDown}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        {/* ── Zeit ── */}
        <ContextMenuGroup>
          {sub('start', <TaskStartIcon size={16} />, t('taskItem.menu.when'))}
          {sub('deadline', <TaskDeadlineIcon size={16} />, t('taskItem.menu.deadline'))}
          {sub('duration', <DurationIcon size={16} />, t('taskItem.menu.duration'))}
          {parentRecurring ? <RecurrenceLockedRow /> : sub('recurrence', <RecurringIcon size={16} />, t('taskItem.menu.recurrence'))}
          <ReminderMenuSub
            taskId={reminderTaskId}
            // Ohne eigene Abfrage je Zeile kein Haken — die Glocke an der Zeile sagt es.
            marked={false}
            open={openSub === 'reminders'}
            onOpenChange={(next) => setOpenSub(next ? 'reminders' : null)}
            onOpenDetails={onOpenReminders}
          />
        </ContextMenuGroup>

        <ContextMenuSeparator />

        {/* ── Einordnung ── */}
        <ContextMenuGroup>
          {sub('attention', <TaskAttentionIcon size={16} />, t('taskItem.menu.attention'))}
          {sub('tags', <TagIcon size={16} />, t('taskItem.menu.tags'))}
          {sub('reward', <MotivationIcon size={16} />, t('taskItem.menu.reward'))}
        </ContextMenuGroup>

        {/* ── Die Unteraufgabe selbst: ruhen, heraus aus der Aufgabe oder in eine andere ── */}
        {(onRest || onUnnest || canMove || onIndent || onOutdent) && (
          <>
            <ContextMenuSeparator />
            <ContextMenuGroup>
              {onRest && (dormant ? (
                <MenuAction
                  icon={<ReactivateIcon size={16} />}
                  label={t('taskItem.menu.wakeNow')}
                  onSelect={() => onRest(false)}
                />
              ) : (
                <MenuAction
                  icon={<DormantIcon size={16} />}
                  label={t('taskItem.menu.rest')}
                  onSelect={() => onRest(true)}
                />
              ))}
              {onIndent && (
                <MenuAction
                  icon={<ListIndentIncrease size={16} strokeWidth={2} />}
                  label={t('taskItem.subtaskTree.indent')}
                  shortcut={shortcutText('⌥→')}
                  onSelect={onIndent}
                />
              )}
              {onOutdent && (
                <MenuAction
                  icon={<ListIndentDecrease size={16} strokeWidth={2} />}
                  label={t('taskItem.subtaskTree.outdent')}
                  shortcut={shortcutText('⌥←')}
                  onSelect={onOutdent}
                />
              )}
              {onUnnest && (
                <MenuAction
                  icon={<IndentDecrease size={16} strokeWidth={2} />}
                  label={t('taskList.nest.unnest')}
                  shortcut={shortcutText('⇧⌥↓')}
                  onSelect={onUnnest}
                />
              )}
              {canMove && (
                <MenuSub
                  icon={<IndentIncrease size={16} strokeWidth={2} />}
                  label={t('taskList.nest.move')}
                  open={moveOpen}
                  onOpenChange={setMoveOpen}
                  marked={false}
                >
                  {moveOpen && (
                    <MoveTargets
                      parentId={parentId}
                      movingRecurring={movingRecurring}
                      onSelect={(target) => { onMoveTo(target); close() }}
                      onClose={close}
                    />
                  )}
                </MenuSub>
              )}
            </ContextMenuGroup>
          </>
        )}

        <ContextMenuSeparator />

        <MenuAction
          danger
          icon={<Trash2 size={16} strokeWidth={2} />}
          label={t('taskItem.menu.delete')}
          shortcut={shortcutText(TASK_MENU_SHORTCUTS.delete)}
          onSelect={onDelete}
        />
      </ContextMenuContent>
    </ContextMenu>
    {shortcut && (
      <TaskMenuShortcutBox
        target={shortcut}
        task={entry}
        handlers={handlers}
        onRest={onRest && !dormant ? () => onRest(true) : undefined}
        onClose={closeShortcut}
        anchor="below"
      />
    )}
    </>
  )
}
